import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/layout/protected-route';
import MainLayout from './components/layout/main-layout';
import NotificationInitializer from './components/notifications/notification-initializer';
import PushPermissionGate from './components/notifications/push-permission-gate';
import ToastContainer from './components/notifications/toast-container';
import { ToastProvider } from './contexts/toast-context';
import Login from './pages/login';

// Pages - Static imports
import Dashboard from './pages/dashboard';
import PlatformDashboard from './pages/platform-dashboard/platform-dashboard';
import DataExplorer from './pages/data-explorer/data-explorer';
import { PermissionGuard } from './components/guards/PermissionGuard';
import { PERMISSIONS } from './constants/permissions';
import ReviewCenter from './pages/review-center/review-center';
import ErrorMasterForm from './pages/error-master/error-master-form';
import ErrorMasterList from './pages/error-master/error-master-list';
import ErrorMasterView from './pages/error-master/error-master-view';
import EmailOutboxList from './pages/email/email-outbox-list';
import EmailOutboxView from './pages/email/email-outbox-view';
import EmailCompose from './pages/email/email-compose';
import PersonnelForm from './pages/personnel/personnel-form';
import PersonnelList from './pages/personnel/personnel-list';
import PersonnelView from './pages/personnel/personnel-view';
import UnitVillageForm from './pages/unit-villages/unit-village-form';
import UnitVillageView from './pages/unit-villages/unit-village-view';
import UnitVillagesList from './pages/unit-villages/unit-villages-list';
import UnitForm from './pages/units/unit-form';
import UnitView from './pages/units/unit-view';
import ValueSetForm from './pages/value-sets/value-set-form';
import ValueSetView from './pages/value-sets/value-set-view';
import ValueSetsList from './pages/value-sets/value-sets-list';
import AppConfigList from './pages/app-config/app-config-list';
import AppConfigForm from './pages/app-config/app-config-form';
import AppConfigView from './pages/app-config/app-config-view';
import ProviderConfig from './pages/app-config/provider-config';
import RbacDashboard from './pages/rbac/rbac-dashboard';
import PromptForm from './pages/prompts/prompt-form';
import PromptList from './pages/prompts/prompt-list';
import PromptView from './pages/prompts/prompt-view';
import AiModelList from './pages/ai-models/ai-model-list';
import AiModelForm from './pages/ai-models/ai-model-form';
import AiModelView from './pages/ai-models/ai-model-view';
import UploadDocument from './pages/document-intelligence/upload-document';
import SavedDocuments from './pages/document-intelligence/saved-documents';
import DocumentResult from './pages/document-intelligence/document-result';
import DocumentMasterList from './pages/document-intelligence/document-master-list';
import DocumentMasterForm from './pages/document-intelligence/document-master-form';
import DocumentMasterView from './pages/document-intelligence/document-master-view';
import AgentList from './pages/agents/agent-list';
import AgentForm from './pages/agents/agent-form';
import AgentView from './pages/agents/agent-view';
import ConversationsList from './pages/conversations/conversations-list';
import ConversationView from './pages/conversations/conversation-view';
import ChatWorkspace from './pages/conversations/chat-workspace';
import EvalList from './pages/evals/eval-list';
import EvalForm from './pages/evals/eval-form';
import EvalView from './pages/evals/eval-view';
import EvalCaseList from './pages/eval-cases/eval-case-list';
import EvalCaseForm from './pages/eval-cases/eval-case-form';
import EvalCaseView from './pages/eval-cases/eval-case-view';
import FunctionList from './pages/functions/function-list';
import FunctionForm from './pages/functions/function-form';
import FunctionView from './pages/functions/function-view';
// Notification engine — the configuration-driven model.
import NotificationTypeList from './pages/notification-types/notification-type-list';
import NotificationTypeForm from './pages/notification-types/notification-type-form';
import NotificationTypeView from './pages/notification-types/notification-type-view';
import RecipientMasterList from './pages/recipient-masters/recipient-master-list';
import RecipientMasterForm from './pages/recipient-masters/recipient-master-form';
import RecipientMasterView from './pages/recipient-masters/recipient-master-view';
import RecipientListList from './pages/recipient-lists/recipient-list-list';
import RecipientListForm from './pages/recipient-lists/recipient-list-form';
import RecipientListView from './pages/recipient-lists/recipient-list-view';
import ActionMasterList from './pages/action-masters/action-master-list';
import ActionMasterForm from './pages/action-masters/action-master-form';
import ActionMasterView from './pages/action-masters/action-master-view';
import NotificationSchedulesTab from './pages/schedules/notification-schedules-tab';
import ScheduleForm from './pages/schedules/schedule-form';
import ScheduleView from './pages/schedules/schedule-view';
import NotificationRunList from './pages/notification-runs/notification-run-list';
import NotificationPreferences from './pages/notification-preferences/notification-preferences';
import NotificationRunView from './pages/notification-runs/notification-run-view';
// The `Engine` prefix is now historical: the legacy notification hub and its
// master CRUD have been removed, so these are the only notification screens
// there are. Kept as-is rather than renamed, because the alias appears in the
// routes below and a rename here buys nothing.
import EngineNotificationMasterList from './pages/notification-masters/notification-master-list';
import EngineNotificationMasterForm from './pages/notification-masters/notification-master-form';
import EngineNotificationMasterView from './pages/notification-masters/notification-master-view';
import AiObservability from './pages/ai-observability/ai-observability';
import ObsMaster from './pages/ai-observability/obs-master';
import ObsTracing from './pages/ai-observability/obs-tracing';
import ObsTracingDetail from './pages/ai-observability/obs-tracing-detail';
import EvalRunList from './pages/eval-runs/eval-run-list';
import EvalRunView from './pages/eval-runs/eval-run-view';
import KnowledgeBaseList from './pages/knowledge-base/knowledge-base-list';
import KnowledgeBaseForm from './pages/knowledge-base/knowledge-base-form';
import KnowledgeBaseView from './pages/knowledge-base/knowledge-base-view';
import PipelineList from './pages/pipelines/pipeline-list';
import PipelineForm from './pages/pipelines/pipeline-form';
import PipelineView from './pages/pipelines/pipeline-view';
import PipelineRunList from './pages/pipeline-runs/pipeline-run-list';
import PipelineRunView from './pages/pipeline-runs/pipeline-run-view';
import RagList from './pages/rags/rag-list';
import RagForm from './pages/rags/rag-form';
import RagView from './pages/rags/rag-view';
import DistrictsList from './pages/districts/districts-list';
import DistrictForm from './pages/districts/district-form';
import DistrictView from './pages/districts/district-view';
import UnitTypesList from './pages/unit-types/unit-type-list';
import UnitTypeForm from './pages/unit-types/unit-type-form';
import UnitTypeView from './pages/unit-types/unit-type-view';
import DepartmentsList from './pages/departments/department-list';
import DepartmentForm from './pages/departments/department-form';
import DepartmentView from './pages/departments/department-view';
import MinistriesList from './pages/ministries/ministry-list';
import MinistryForm from './pages/ministries/ministry-form';
import MinistryView from './pages/ministries/ministry-view';
import ProjectsList from './pages/projects/project-list';
import ProjectForm from './pages/projects/project-form';
import ProjectView from './pages/projects/project-view';
import LogMasterList from './pages/log-master/log-master-list';
import LogMasterForm from './pages/log-master/log-master-form';
import LogMasterView from './pages/log-master/log-master-view';

import NotFound from './pages/not-found';
import DesignationMasterList from './pages/designation-master/designation-master-list';
import DesignationMasterForm from './pages/designation-master/designation-master-form';
import DesignationMasterView from './pages/designation-master/designation-master-view';
import LogTransactionList from './pages/log-transaction/log-transaction-list';
import ErrorLogsMonitorList from './pages/error-logs-monitor/error-logs-monitor-list';
import ErrorAnalytics from './pages/error-analytics/error-analytics';
import PromptExecutionsList from './pages/prompt-executions/prompt-executions-list';
import OrgStructureView from './pages/org-structure/org-structure-view';
import ProfileView from './pages/profile/profile-view';
import NavigationSettings from './pages/settings/navigation-settings';
import DisplaySettings from './pages/settings/display-settings';
import UserManagementList from './pages/user-management/user-management-list';
import UserManagementForm from './pages/user-management/user-management-form';
import UserManagementView from './pages/user-management/user-management-view';
import UserManagementGroupView from './pages/user-management/user-management-group-view';
import UserManagementGroupForm from './pages/user-management/user-management-group-form';

// Merged "group" screens (tabbed wrappers around existing lists)
import UnitsGroup from './pages/units/units-group';
import OrganizationGroup from './pages/organization/organization-group';
import PromptsGroup from './pages/prompts/prompts-group';
import NotificationEngineGroup from './pages/notification-engine/notification-engine-group';
import BackgroundJobsGroup from './pages/background-jobs/background-jobs-group';
import BackgroundJobForm from './pages/background-jobs/job-form';
import BackgroundScheduleForm from './pages/background-jobs/schedule-form';
import BackgroundJobView from './pages/background-jobs/job-view';
import BackgroundScheduleView from './pages/background-jobs/schedule-view';
import BackgroundRunView from './pages/background-jobs/run-view';
import PartnersGroup from './pages/partners/partners-group';
import PartnerForm from './pages/partners/partner-form';
import PartnerView from './pages/partners/partner-view';
import ConnectionTypeForm from './pages/partners/connection-type-form';
import PartnerConnectionForm from './pages/partners/partner-connection-form';
import ConnectionTypeView from './pages/partners/connection-type-view';
import FileUploadList from './pages/file-upload/file-upload-list';
import LogsGroup from './pages/logs/logs-group';
import FeedbackGroup from './pages/feedback/feedback-group';
import AiFeedbackGroup from './pages/ai-feedback/ai-feedback-group';
import FeedbackMasterForm from './pages/feedback-master/feedback-master-form';
import FeedbackMasterView from './pages/feedback-master/feedback-master-view';

export default function CoreService() {
  return (
    <ToastProvider>
      {/* Auto-registers web push (FCM) on login; persists across route changes. */}
      <NotificationInitializer />
      {/* Mandatory permission prompt after login (only when Firebase is configured). */}
      <PushPermissionGate />
      {/* Renders in-app toast popups pushed by the notification bell. */}
      <ToastContainer />
      <Routes>
      {/* Login Route */}
      <Route path="/login" element={<Login />} />

      {/* All routes are protected */}
      {/* The landing page: AI, Software Engineering and Unit Hierarchy, each as
          a band of its own. Figures are placeholder until the rollup endpoints
          exist — the screen labels itself "illustrative" so nobody mistakes
          them for live numbers. */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PlatformDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Data Explorer — the drill-down behind the dashboard's Explore Data
          button: projects, then their collections, then the rows. Read-only;
          the API decides what is reachable and redacts credential fields. */}
      <Route
        path="/data-explorer"
        element={
          <ProtectedRoute>
            <MainLayout>
              {/* Guarded here too, not only by the hidden button: typing the URL
                  would otherwise reach a page whose every request 403s, which
                  reads as broken rather than as not-for-you. */}
              <PermissionGuard
                permission={PERMISSIONS.DATA_EXPLORER_READ}
                resourceName="the Project Explorer"
              >
                <DataExplorer />
              </PermissionGuard>
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* The Review Center used to be what /dashboard served. It is unchanged
          and still reachable here — only the landing page moved off it. */}
      <Route
        path="/review-center"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReviewCenter />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy master-data counts dashboard — preserved, not linked in the menu. */}
      <Route
        path="/analytics-dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Logs Route */}
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogsGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Error Logs Route — now the Error Analytics dashboard */}
      <Route
        path="/error-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Error Analytics Route (same page, alternate path) */}
      <Route
        path="/error-analytics"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy Error Logs Monitor (preserved) */}
      <Route
        path="/error-logs-monitor"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorLogsMonitorList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Units Routes */}
      <Route
        path="/units"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitsGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/units/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/units/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/units/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Personnel Routes */}
      <Route
        path="/personnel"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PersonnelList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/personnel/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PersonnelForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/personnel/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PersonnelView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/personnel/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PersonnelForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Unit Villages Routes */}
      <Route
        path="/unit-villages"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitVillagesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-villages/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitVillageForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-villages/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitVillageView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-villages/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitVillageForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Application Configuration Routes — beside Project Settings, which now
          renders the moved blocks read-only and links here. */}
      <Route
        path="/app-config"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppConfigList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Ahead of /app-config/:configKey — a literal segment must be matched before
          the parameterised one, or "providers" is read as a config key. The
          optional :channel preselects one gateway; without it the screen opens on
          the first configured provider. */}
      <Route
        path="/app-config/providers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProviderConfig />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-config/providers/:channel"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProviderConfig />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-config/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppConfigForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-config/:configKey/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppConfigForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app-config/:configKey"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppConfigView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Value Sets Routes */}
      <Route
        path="/value-sets"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ValueSetsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/value-sets/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ValueSetForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/value-sets/:key/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ValueSetForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/value-sets/:key"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ValueSetView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Error Master Routes */}
      <Route
        path="/error-master"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorMasterList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/error-master/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/error-master/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorMasterView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/error-master/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ErrorMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Prompts Routes */}
      <Route
        path="/prompt-table"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompt-table/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompt-table/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompt-table/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Document Intelligence Routes */}
      <Route
        path="/document-intelligence"
        element={<Navigate to="/document-intelligence/upload" replace />}
      />
      <Route
        path="/document-intelligence/upload"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UploadDocument />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/documents/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentResult />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* A run that has just been processed and not saved yet. It arrives in
          router state rather than by id, because there is no row to fetch
          until the operator decides to keep it. */}
      <Route
        path="/document-intelligence/result"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentResult />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/saved"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SavedDocuments />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/master"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentMasterList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/master/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/master/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentMasterView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-intelligence/master/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* AI Models Routes */}
      <Route
        path="/ai-models"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiModelList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-models/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiModelForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-models/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiModelView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-models/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiModelForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* RAG Core Component Routes */}
      <Route
        path="/rags"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RagList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rags/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RagForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rags/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RagView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rags/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RagForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Knowledge Base Routes — the corpus a pipeline indexes */}
      <Route
        path="/knowledge-bases"
        element={
          <ProtectedRoute>
            <MainLayout>
              <KnowledgeBaseList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-bases/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <KnowledgeBaseForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-bases/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <KnowledgeBaseView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-bases/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <KnowledgeBaseForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Pipeline Core Component Routes */}
      <Route
        path="/pipelines"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipelines/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipelines/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipelines/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-runs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineRunList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline-runs/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PipelineRunView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Eval Case Library Routes */}
      <Route
        path="/eval-cases"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalCaseList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eval-cases/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalCaseForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eval-cases/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalCaseView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eval-cases/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalCaseForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Function Master Routes */}
      <Route path="/functions" element={<ProtectedRoute><MainLayout><FunctionList /></MainLayout></ProtectedRoute>} />
      <Route path="/functions/create" element={<ProtectedRoute><MainLayout><FunctionForm /></MainLayout></ProtectedRoute>} />
      <Route path="/functions/:id" element={<ProtectedRoute><MainLayout><FunctionView /></MainLayout></ProtectedRoute>} />
      <Route path="/functions/:id/edit" element={<ProtectedRoute><MainLayout><FunctionForm /></MainLayout></ProtectedRoute>} />

      {/* Notification engine */}
      <Route path="/notification-masters" element={<ProtectedRoute><MainLayout><EngineNotificationMasterList /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-masters/create" element={<ProtectedRoute><MainLayout><EngineNotificationMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-masters/:id" element={<ProtectedRoute><MainLayout><EngineNotificationMasterView /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-masters/:id/edit" element={<ProtectedRoute><MainLayout><EngineNotificationMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-types" element={<ProtectedRoute><MainLayout><NotificationTypeList /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-types/create" element={<ProtectedRoute><MainLayout><NotificationTypeForm /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-types/:id" element={<ProtectedRoute><MainLayout><NotificationTypeView /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-types/:id/edit" element={<ProtectedRoute><MainLayout><NotificationTypeForm /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-masters" element={<ProtectedRoute><MainLayout><RecipientMasterList /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-masters/create" element={<ProtectedRoute><MainLayout><RecipientMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-masters/:id" element={<ProtectedRoute><MainLayout><RecipientMasterView /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-masters/:id/edit" element={<ProtectedRoute><MainLayout><RecipientMasterForm /></MainLayout></ProtectedRoute>} />
      {/* Self-service: the caller's own preferences, scoped by the backend.
          No permission gate — the people this is for are exactly the people a
          configuration permission would exclude. */}
      <Route path="/notification-preferences" element={<ProtectedRoute><MainLayout><NotificationPreferences /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-lists" element={<ProtectedRoute><MainLayout><RecipientListList /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-lists/create" element={<ProtectedRoute><MainLayout><RecipientListForm /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-lists/:id" element={<ProtectedRoute><MainLayout><RecipientListView /></MainLayout></ProtectedRoute>} />
      <Route path="/recipient-lists/:id/edit" element={<ProtectedRoute><MainLayout><RecipientListForm /></MainLayout></ProtectedRoute>} />
      <Route path="/action-masters" element={<ProtectedRoute><MainLayout><ActionMasterList /></MainLayout></ProtectedRoute>} />
      <Route path="/action-masters/create" element={<ProtectedRoute><MainLayout><ActionMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/action-masters/:id" element={<ProtectedRoute><MainLayout><ActionMasterView /></MainLayout></ProtectedRoute>} />
      <Route path="/action-masters/:id/edit" element={<ProtectedRoute><MainLayout><ActionMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/schedules" element={<ProtectedRoute><MainLayout><NotificationSchedulesTab /></MainLayout></ProtectedRoute>} />
      <Route path="/schedules/create" element={<ProtectedRoute><MainLayout><ScheduleForm /></MainLayout></ProtectedRoute>} />
      <Route path="/schedules/:id/edit" element={<ProtectedRoute><MainLayout><ScheduleForm /></MainLayout></ProtectedRoute>} />
      <Route path="/schedules/:id" element={<ProtectedRoute><MainLayout><ScheduleView /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-runs" element={<ProtectedRoute><MainLayout><NotificationRunList /></MainLayout></ProtectedRoute>} />
      <Route path="/notification-runs/:id" element={<ProtectedRoute><MainLayout><NotificationRunView /></MainLayout></ProtectedRoute>} />

      {/* Evals Routes */}
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AgentList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AgentForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AgentForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AgentView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Conversations Routes */}
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChatWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evals"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evals/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evals/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evals/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* AI Observability — cost / latency / retrieval rollup across the AI layer */}
      <Route
        path="/ai-observability"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiObservability />
            </MainLayout>
          </ProtectedRoute>
        }
      />





      <Route
        path="/ai-observability/master"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ObsMaster />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-observability/tracing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ObsTracing />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-observability/tracing/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ObsTracingDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Retired pages. The Overview and a call's own page (its layers) cover
          what they showed; kept as redirects so a bookmarked link still lands. */}
      <Route path="/ai-observability/prompts/*" element={<Navigate to="/ai-observability" replace />} />
      <Route path="/ai-observability/executions/*" element={<Navigate to="/ai-observability/tracing" replace />} />
      <Route path="/ai-observability/traces/*" element={<Navigate to="/ai-observability/tracing" replace />} />
      <Route path="/ai-observability/operations" element={<Navigate to="/ai-observability/tracing" replace />} />

      {/* Eval Runs Routes (history) */}
      <Route
        path="/eval-runs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalRunList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/eval-runs/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EvalRunView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Districts Routes */}
      <Route
        path="/districts"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DistrictsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/districts/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DistrictForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/districts/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DistrictView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/districts/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DistrictForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />


      {/* Unit Types Routes */}
      <Route
        path="/unit-types"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitTypesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-types/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitTypeForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-types/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitTypeView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unit-types/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnitTypeForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Departments Routes */}
      <Route
        path="/departments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Ministries Routes */}
      <Route
        path="/ministries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MinistriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ministries/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MinistryForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ministries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MinistryView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ministries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MinistryForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Projects Routes (formerly Apps) */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy /apps -> /projects redirects (keep old bookmarks working) */}
      <Route path="/apps" element={<Navigate to="/projects" replace />} />
      <Route path="/apps/create" element={<Navigate to="/projects/create" replace />} />
      <Route path="/apps/:id" element={<Navigate to="/projects" replace />} />
      <Route path="/apps/:id/edit" element={<Navigate to="/projects" replace />} />


      {/* Log Master Routes */}
      <Route
        path="/log-master"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogMasterList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/log-master/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/log-master/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogMasterView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/log-master/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Designation Master Routes - Full CRUD with name and designationCd */}
      {/* List: /designation-master - Shows all designations */}
      <Route
        path="/designation-master"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DesignationMasterList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Create: /designation-master/create - Create new designation */}
      <Route
        path="/designation-master/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DesignationMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* View: /designation-master/:id - Shows designation details */}
      <Route
        path="/designation-master/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DesignationMasterView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Edit: /designation-master/:id/edit - Updates designation */}
      <Route
        path="/designation-master/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DesignationMasterForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Log Transaction Routes - Application Logs Monitor */}
      <Route
        path="/log-transaction"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LogTransactionList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Prompt Executions Routes - AI Prompts Monitor */}
      <Route
        path="/prompt-executions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptExecutionsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Notification Engine — merged, the same shape as Notifications: the
          engine's own inbox, preferences, record and configuration in one place. */}
      <Route
        path="/notification-engine"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationEngineGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Organization — merged: Ministries · Departments · Districts · Org Structure */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrganizationGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Prompts — merged: Prompt Master · Executions */}
      <Route
        path="/prompts"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PromptsGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />


      {/* Organizational Structure Route */}
      <Route
        path="/org-structure"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrgStructureView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* User Management Routes */}
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management/group/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementGroupView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management/group/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementGroupForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagementForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* My Profile (read-only) */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfileView />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings. No permission gate: every signed-in user has a sidebar and a
          theme, and these screens only ever act on what that user can already
          see. `/settings` lands on the first section rather than 404ing. */}
      <Route path="/settings" element={<Navigate to="/settings/navigation" replace />} />
      <Route
        path="/settings/navigation"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NavigationSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/display"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DisplaySettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Access Control (RBAC) */}
      <Route
        path="/access-control"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RbacDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Feedback — definitions, submissions and reviews on one tabbed screen.
          The old /feedback-master path still lands here so existing links work. */}
      <Route path="/feedback" element={<ProtectedRoute><MainLayout><FeedbackGroup /></MainLayout></ProtectedRoute>} />
      <Route path="/ai-feedback" element={<ProtectedRoute><MainLayout><AiFeedbackGroup /></MainLayout></ProtectedRoute>} />
      <Route path="/feedback-master" element={<Navigate to="/feedback?tab=definitions" replace />} />
      <Route path="/feedback-master/create" element={<ProtectedRoute><MainLayout><FeedbackMasterForm /></MainLayout></ProtectedRoute>} />
      <Route path="/feedback-master/:id" element={<ProtectedRoute><MainLayout><FeedbackMasterView /></MainLayout></ProtectedRoute>} />
      <Route path="/feedback-master/:id/edit" element={<ProtectedRoute><MainLayout><FeedbackMasterForm /></MainLayout></ProtectedRoute>} />

      {/* Background Jobs */}
      <Route
        path="/background-jobs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundJobsGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Create/edit are pages of their own — same shape as error-master and the
          other masters — so unsaved edits are guarded by useNavigationBlocker
          instead of being lost to a modal backdrop click. */}
      <Route
        path="/background-jobs/jobs/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundJobForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/background-jobs/jobs/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundJobForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/background-jobs/schedules/create"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundScheduleForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/background-jobs/schedules/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BackgroundScheduleForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Detail pages — the same list → view → edit shape as the notification
          masters. A job's page runs it and shows its schedules and runs; a run's
          page shows every attempt of that fire; a schedule's page shows its
          timing and the runs it produced. */}
      <Route path="/background-jobs/jobs/:id" element={<ProtectedRoute><MainLayout><BackgroundJobView /></MainLayout></ProtectedRoute>} />
      <Route path="/background-jobs/schedules/:id" element={<ProtectedRoute><MainLayout><BackgroundScheduleView /></MainLayout></ProtectedRoute>} />
      <Route path="/background-jobs/runs/:id" element={<ProtectedRoute><MainLayout><BackgroundRunView /></MainLayout></ProtectedRoute>} />

      {/* Partners & Integrations (tabbed: partners, connections, connection types) */}
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PartnersGroup />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Partner full-page create / edit / view (siblings of the tabbed group) */}
      <Route path="/partners/create" element={<ProtectedRoute><MainLayout><PartnerForm /></MainLayout></ProtectedRoute>} />
      {/* Connection Types (Partners → Connection Types tab) full-page create/edit/view */}
      {/* Declared before /partners/:id so "connections" is not read as a partner id. */}
      <Route path="/partners/connections/create" element={<ProtectedRoute><MainLayout><PartnerConnectionForm /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/connections/:id/edit" element={<ProtectedRoute><MainLayout><PartnerConnectionForm /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/connection-types/create" element={<ProtectedRoute><MainLayout><ConnectionTypeForm /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/connection-types/:id/edit" element={<ProtectedRoute><MainLayout><ConnectionTypeForm /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/connection-types/:id" element={<ProtectedRoute><MainLayout><ConnectionTypeView /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/:id/edit" element={<ProtectedRoute><MainLayout><PartnerForm /></MainLayout></ProtectedRoute>} />
      <Route path="/partners/:id" element={<ProtectedRoute><MainLayout><PartnerView /></MainLayout></ProtectedRoute>} />

      {/* File Uploads */}
      <Route
        path="/file-uploads"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FileUploadList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="dashboard" replace />} />

      {/* 404 Not Found */}
      {/* Email */}
      <Route
        path="/email/outbox"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmailOutboxList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email/compose"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmailCompose />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email/outbox/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmailOutboxView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </ToastProvider>
  );
}
